import { ModalTypeEnum, PlusIcon, SearchBar, SmallButton, Table, useAlert, useModal } from "@wit/mpesa-ui-components";
import { TableCell } from "@wit/mpesa-ui-components/lib/components/table/table-row/table-row.styled";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled, { ThemeContext } from "styled-components";
import { IStoreInterface } from "../../../configs/store.config";
import useCheckFeatureAvailable from "../../../shared/hooks/use-check-available-feature";
import { IBusiness } from "../../../shared/models/business.model";
import { FEATURES } from "../../../shared/renderer.utils";
import { SearchBarFilterContainer } from "../../../shared/responsive-ui.styled";
import { IconContainer, Row } from "../../../shared/shared.styled";
import { getBusinessColumns, renderTableBusinessButtons } from "./business-utils";
import { BusinessesAPI } from "./business.api";
import EditBusinessModal from "./edit-business.modal";
import { canUserFromSFCMarketAddOrEditOrDeleteBusiness } from "./utils/check-user-roles.util";

interface IBusinessListProps {
  getNewData: () => void;
}

/**
 * BusinessList component
 */
const BusinessList = ({ getNewData }: IBusinessListProps) => {
  const userRole = useSelector((state: IStoreInterface) => state.authenticationReducer.loggedUser?.role);
  const [t] = useTranslation();
  const [selectedBusiness, setSelectedBusiness] = React.useState<IBusiness | undefined>(undefined);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [results, setResults] = React.useState<IBusiness[]>();
  const [showAlert, , setAlertProps] = useAlert();
  const themeContext = useContext(ThemeContext);
  const showCategories = useCheckFeatureAvailable(FEATURES.BUSINESS_CATEGORIES);
  const {
    businesses: { data, lastVersion },
    categories,
  } = useSelector((state: IStoreInterface) => state.businessesReducer);

  const [showEditModal, hideEditModal, setEditProps] = useModal(
    ModalTypeEnum.CustomModal,
    undefined,
    <EditBusinessModal
      onCancelClick={() => hideEditModal()}
      setSelectedBusiness={setSelectedBusiness}
      selectedBusiness={selectedBusiness as IBusiness}
    />,
  );

  const [showAddModal, hidAddModal, setAddProps] = useModal(
    ModalTypeEnum.CustomModal,
    undefined,
    <EditBusinessModal
      onCancelClick={() => hidAddModal()}
      selectedBusiness={selectedBusiness as IBusiness}
      setSelectedBusiness={setSelectedBusiness}
    />,
  );

  /**
   * Sort business pills according to category and business
   */
  const sortBusinessPills = (businessPills: IBusiness[]) => {
    const sorted = businessPills.slice().sort((a, b) => a.categoryId.localeCompare(b.categoryId));

    const groupedPillsByCategoryId: { [categoryId: string]: IBusiness[] } = {};

    for (const object of sorted) {
      const categoryId = object.categoryId;

      if (!groupedPillsByCategoryId[categoryId]) {
        groupedPillsByCategoryId[categoryId] = [];
      }

      groupedPillsByCategoryId[categoryId].push(object);
    }

    for (const categoryId in groupedPillsByCategoryId) {
      // Inline sorting
      groupedPillsByCategoryId[categoryId].sort((a, b) => a.name.localeCompare(b.name));
    }

    // Convert the grouped objects object to an array of arrays.
    const groupedObjectsArray = Object.values(groupedPillsByCategoryId);

    return groupedObjectsArray.flat();
  };

  const businessList = useMemo(() => {
    const _data = results || data;
    const sorted = sortBusinessPills(_data);

    return (
      data &&
      categories && (
        <TableWrapper className="tableWrapper">
          <Table<IBusiness>
            columns={getBusinessColumns(t, categories.data, lastVersion, showCategories)}
            values={sorted}
            displayActions={business =>
              canUserFromSFCMarketAddOrEditOrDeleteBusiness(userRole) &&
              renderTableBusinessButtons<IBusiness>(business, {
                edit: { onClick: editBusiness },
                delete: { onClick: deleteBusiness },
                revert: findLastVersion(business.id) ? { onClick: restoreBusiness } : undefined,
              })
            }
          />
        </TableWrapper>
      )
    );
  }, [results, data]);

  React.useEffect(() => {
    setEditProps({ modalStyles: { width: 615, overflow: "initial" } });
  }, [setEditProps]);

  React.useEffect(() => {
    setAddProps({ modalStyles: { width: 615, overflow: "initial" } });
  }, [setAddProps]);

  React.useEffect(() => {
    if (searchTerm && searchTerm !== "") {
      setResults(
        data.filter(
          e =>
            e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            e.shortCode.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    } else {
      setResults(data);
    }
  }, [data, searchTerm]);

  /**
   * Edit business pill
   */
  const editBusiness = (business: IBusiness) => {
    setSelectedBusiness(business);
    showEditModal();
  };

  /**
   * Delete business bill
   */
  const deleteBusiness = (business: IBusiness) => {
    BusinessesAPI.methods
      .deleteBusiness(business)
      .then(
        _res => {
          setAlertProps({
            title: t("pages.configurations.businessTab.success.deleteBusinessesSuccess"),
            type: AlertTypeEnum.SUCCESS,
          });
          showAlert();
        },
        _err => {
          setAlertProps({
            title: t("pages.configurations.businessTab.errors.deleteBusinessesError"),
            type: AlertTypeEnum.ERROR,
          });
          showAlert();
        },
      )
      .then(() => getNewData());
  };

  /**
   * Restore Business pill
   */
  const restoreBusiness = (business: IBusiness) => {
    BusinessesAPI.methods
      .restoreBusiness(business)
      .then(
        _res => {
          getNewData();
          setAlertProps({
            title: t("pages.configurations.businessTab.success.restoreBusinessesSuccess"),
            type: AlertTypeEnum.SUCCESS,
          });
          showAlert();
        },
        _err => {
          setAlertProps({
            title: t("pages.configurations.businessTab.errors.restoreBusinessesError"),
            type: AlertTypeEnum.ERROR,
          });
          showAlert();
        },
      )
      .then(() => getNewData());
  };

  /**
   * Create new business pill
   */
  const createNewBusiness = () => {
    setSelectedBusiness(undefined);
    showAddModal();
  };

  /**
   * Find last version of business pills
   */
  const findLastVersion = (id: number | undefined) => {
    if (!id) {
      return undefined;
    }
    return lastVersion.find(b => b.id === id) || null;
  };

  return (
    <BusinessListContainer>
      <Row style={{ justifyContent: "space-between", gap: "24px", flexWrap: "wrap", marginBottom: "24px" }}>
        <SearchBarFilterContainer>
          <SearchBar
            placeholderLabel={t("pages.configurations.businessTab.businessListPage.search")}
            value={searchTerm || ""}
            onChange={e => setSearchTerm(e.target.value)}
            clearValue={() => setSearchTerm("")}
          />
        </SearchBarFilterContainer>
        {canUserFromSFCMarketAddOrEditOrDeleteBusiness(userRole) && (
          <div id={"add-new-business"}>
            <SmallButton
              iconComponent={
                <IconContainer size={16} color={themeContext.palette.vodafoneRed}>
                  <PlusIcon />
                </IconContainer>
              }
              onClick={() => createNewBusiness()}
              titleLabel={t("pages.configurations.businessTab.addNewBusiness")}
            />
          </div>
        )}
      </Row>
      {businessList}
    </BusinessListContainer>
  );
};

export default BusinessList;

const TableWrapper = styled.div`
  > div:first-child {
    > div:last-child {
      > div {
        border-bottom: 1px solid;
        border-bottom-color: ${props => props.theme.palette.lightGrey};
      }
    }
  }
`;

const BusinessListContainer = styled.div`
  margin-top: 36px;
  margin-bottom: 50px;
  ${TableCell}:last-of-type {
    margin-right: 0 !important;
  }
`;

export const Label = styled("span")`
  font-family: Vodafone Rg;
  font-size: 16px;
  font-weight: bold;
  color: ${props => props.theme.palette.midGrey};
`;
